/* ==========================================================================
   Store OS · identity & user-management contracts (mirrors backend §9.7/§13.7)
   ========================================================================== */

export const ROLE_KEYS = [
  'team_member',
  'shift_leader',
  'assistant_manager',
  'general_manager',
  'district_manager',
  'operations_manager',
  // Operator-only: never offered as a choice anywhere in the UI (D-39).
  'system_admin',
] as const

export type RoleKey = (typeof ROLE_KEYS)[number]

/** Formal titles, as the GM writes them: a role is a title, so both words are capitalised. */
export const ROLE_LABELS: Record<RoleKey, string> = {
  team_member: 'Team Member',
  shift_leader: 'Shift Leader',
  assistant_manager: 'Assistant Manager',
  general_manager: 'General Manager',
  district_manager: 'District Manager',
  operations_manager: 'Operations Manager',
  system_admin: 'System Admin',
}

export interface AuthUser {
  id: string
  email: string
  full_name: string
  /** Everyday name (D-44); null means “use the full legal name”. */
  nickname: string | null
  /** Small inline profile picture (data URL), or null for initials. */
  avatar: string | null
  role: RoleKey
  store_id: number | null
  region: string | null
  scope_store_ids: number[] | null
  is_platform_admin: boolean
  email_verified: boolean
  two_factor_enabled: boolean
  /** Server-side mandatory-2FA policy for this account. */
  two_factor_required: boolean
  must_change_password: boolean
  /** Effective permission keys from the backend catalogue. */
  capabilities: string[]
  /** Roles this account may grant, strictly below its own rank. */
  grantable_roles: RoleKey[]
  /** GM+ may open the role/permission editor. */
  can_edit_access: boolean
  /** Only the system admin may read activity/audit logs. */
  can_view_audit: boolean
  /** District manager and above may choose shops; everyone else has one store. */
  can_select_shops: boolean
  /** Effective shop scope: null = every shop, [] = none, otherwise those numbers. */
  store_scope: number[] | null
}

export interface AccountProfile {
  full_name: string
  nickname: string | null
  avatar: string | null
  /** Nickname when set, legal name otherwise — decided server-side, one rule (D-44). */
  display_name: string
}

export interface PermissionDefinition {
  key: string
  label: string
  description: string
  group: string
  min_role: RoleKey
}

export interface ManagedUser extends AuthUser {
  is_active: boolean
  is_locked: boolean
  last_login_at: string | null
  created_at: string
  /** Capabilities inherited from the role, before per-user overrides. */
  role_capabilities: string[]
  permission_grants: string[]
  permission_revocations: string[]
}

export interface LoginResult {
  user: AuthUser | null
  mfa_required: boolean
  mfa_ticket: string | null
  access_token_expires_at: string | null
}

export interface ActivityEvent {
  id: string
  event: string
  outcome: 'success' | 'failure' | 'warning' | 'ignored'
  user_id: string | null
  email: string | null
  detail: Record<string, unknown> | null
  created_at: string
}

export interface InviteResult {
  email: string
  role: RoleKey
  store_id: number | null
  expires_in_hours: number
}

export interface TwoFactorSetup {
  secret: string
  otpauth_uri: string
  issuer: string
  account: string
}

/** What Account Settings shows about the signed-in person (§13.8). */
export interface AccountSecurity {
  email: string
  email_verified: boolean
  role: RoleKey
  two_factor_enabled: boolean
  /** Team member, shift leader, assistant manager — they may switch 2FA off. */
  can_remove_2fa: boolean
  /** General manager and above: 2FA is mandatory, only replaceable. */
  required_for_role: boolean
  recovery_codes_remaining: number
  password_changed_at: string | null
}

/** A recent proof of identity: the password, or a code we e-mailed (D-36). */
export interface AccountProof {
  current_password?: string
  verification_code?: string
}
